import React, { useState, useCallback, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { Space, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  PrimaryButton,
  LastUpdatedContainer,
  LastUpdatedLabel,
  LastUpdatedValue,
  TitleIcon,
  StyledButton,
} from './styles';
import StoreStats from './StoreStats';
import StoreTable from './StoreTable';
import StoreForm from './StoreForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import type { StoresProps } from './types';
import type { Store, CreateStorePayload, UpdateStorePayload } from './store/userStoresSlice';
import { LABELS, MESSAGES } from './constants';
import {
  useStores,
  useStoreCategories,
  useStoreSchedules,
  useCreateStore,
  useUpdateStore,
  useDeleteStore,
} from './hooks';

const Stores: React.FC<StoresProps> = () => {
  const { isDarkMode } = useTheme();
  const theme = useMemo(() => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }), [isDarkMode]);

  const { data: stores = [], isLoading: storesLoading, refetch: refetchStores, dataUpdatedAt } = useStores();
  const { data: categories = [] } = useStoreCategories();
  const { data: schedules = [] } = useStoreSchedules();

  const createStoreMutation = useCreateStore();
  const updateStoreMutation = useUpdateStore();
  const deleteStoreMutation = useDeleteStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<{ id: string; name: string } | null>(null);

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleString() : null;

  const handleCreate = useCallback(() => {
    setSelectedStore(null);
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((store: Store) => {
    setSelectedStore(store);
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback((storeId: string) => {
    const store = stores.find((s) => s.id === storeId);
    if (store) {
      setStoreToDelete({ id: storeId, name: store.name });
      setIsDeleteDialogOpen(true);
    }
  }, [stores]);

  const handleFormSubmit = useCallback(
    async (data: CreateStorePayload | UpdateStorePayload) => {
      try {
        if ('id' in data) {
          await updateStoreMutation.mutateAsync(data as UpdateStorePayload);
          message.success(MESSAGES.STORE_UPDATED);
        } else {
          await createStoreMutation.mutateAsync(data);
          message.success(MESSAGES.STORE_CREATED);
        }
        setIsFormOpen(false);
        setSelectedStore(null);
      } catch (error: any) {
        message.error(error || 'Failed to save store');
      }
    },
    [createStoreMutation, updateStoreMutation]
  );

  const handleFormCancel = useCallback(() => {
    setIsFormOpen(false);
    setSelectedStore(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!storeToDelete) return;
    try {
      await deleteStoreMutation.mutateAsync(storeToDelete.id);
      message.success(MESSAGES.STORE_DELETED);
      setIsDeleteDialogOpen(false);
      setStoreToDelete(null);
    } catch (error: any) {
      message.error(error || 'Failed to delete store');
    }
  }, [deleteStoreMutation, storeToDelete]);

  const handleRefresh = useCallback(() => {
    refetchStores();
  }, [refetchStores]);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>
                <TitleIcon>
                  <FontAwesomeIcon icon={faStore} />
                </TitleIcon>
                {LABELS.STORES_MANAGEMENT || 'Stores Management'}
              </Title>
              <Subtitle>
                Manage all your store locations, add new ones, edit existing details, and organize your business operations efficiently.
              </Subtitle>
            </div>
            <Space>
              {lastUpdated && (
                <LastUpdatedContainer>
                  <LastUpdatedLabel>
                    {LABELS.LAST_UPDATED}
                  </LastUpdatedLabel>
                  <LastUpdatedValue>
                    {lastUpdated}
                  </LastUpdatedValue>
                </LastUpdatedContainer>
              )}
              <StyledButton
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={storesLoading}
              >
                {LABELS.REFRESH}
              </StyledButton>
              <PrimaryButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                {LABELS.CREATE_STORE}
              </PrimaryButton>
            </Space>
          </HeaderSection>

          <StoreStats stores={stores} />

          <StoreTable
            stores={stores}
            loading={storesLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {isFormOpen && (
            <StoreForm
              store={selectedStore}
              categories={categories}
              schedules={schedules}
              loading={createStoreMutation.isPending || updateStoreMutation.isPending}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          )}

          {isDeleteDialogOpen && storeToDelete && (
            <DeleteConfirmDialog
              open={isDeleteDialogOpen}
              storeName={storeToDelete.name}
              onConfirm={handleDeleteConfirm}
              onCancel={() => {
                setIsDeleteDialogOpen(false);
                setStoreToDelete(null);
              }}
              loading={deleteStoreMutation.isPending}
            />
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Stores);
